import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckSquare, Clock, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { getStaffDashboard } from "@/lib/dashboardApi";

const StaffDashboard = () => {
  const { userRole, userDepartment, user } = useAuth();

  const [activeCasesCount, setActiveCasesCount] = useState<number | null>(null);
  const [dueTodayCount, setDueTodayCount] = useState<number | null>(null);
  const [overdueCount, setOverdueCount] = useState<number | null>(null);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [assignedCases, setAssignedCases] = useState<any[] | null>(null);
  const [loadingCases, setLoadingCases] = useState(false);

  useEffect(() => {
    const fetchSalesDashboard = async () => {
      if (!user || userDepartment !== "sales") return;
      setLoadingKpis(true);

      try {
        const response = await getStaffDashboard();
        setActiveCasesCount(response.active_cases_count ?? 0);
        setDueTodayCount(response.due_today_count ?? 0);
        setOverdueCount(response.overdue_count ?? 0);
        setAssignedCases(response.assigned_cases ?? []);
      } catch (e) {
        console.error("Erro ao buscar KPIs de vendas:", e);
        setActiveCasesCount(0);
        setDueTodayCount(0);
        setOverdueCount(0);
        setAssignedCases([]);
      } finally {
        setLoadingKpis(false);
        setLoadingCases(false);
      }
    };

    setLoadingCases(true);
    fetchSalesDashboard();
  }, [user, userDepartment]);

  const getDepartmentColor = () => {
    switch (userDepartment) {
      case "sales":
        return "text-sales";
      case "operations":
        return "text-operations";
      case "finance":
        return "text-finance";
      default:
        return "text-primary";
    }
  };

  const getDepartmentLabel = () => {
    switch (userDepartment) {
      case "sales":
        return "Sales";
      case "operations":
        return "Operations";
      case "finance":
        return "Finance";
      default:
        return "Department";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">My Dashboard</h2>
        <p className="text-muted-foreground">
          {getDepartmentLabel()} Department - Your assigned tasks and cases
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Active Cases</CardTitle>
            <CheckSquare className={`h-4 w-4 ${getDepartmentColor()}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {userDepartment === "sales" ? (
                loadingKpis || activeCasesCount === null ? "—" : activeCasesCount
              ) : (
                8
              )}
            </div>
            <p className="text-xs text-muted-foreground">Assigned to you</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Today</CardTitle>
            <Clock className="h-4 w-4 text-finance" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {userDepartment === "sales" ? (
                loadingKpis || dueTodayCount === null ? "—" : dueTodayCount
              ) : (
                3
              )}
            </div>
            <p className="text-xs text-muted-foreground">Tasks requiring action</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {userDepartment === "sales" ? (
                loadingKpis || overdueCount === null ? "—" : overdueCount
              ) : (
                1
              )}
            </div>
            <p className="text-xs text-muted-foreground">Needs immediate attention</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>My Assigned Cases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {userDepartment === "sales" && (
              <>
                {loadingCases && (
                  <div className="text-sm text-muted-foreground">Carregando casos...</div>
                )}
                {!loadingCases && assignedCases && assignedCases.length === 0 && (
                  <div className="text-sm text-muted-foreground">Nenhum caso atribuído.</div>
                )}
                {!loadingCases && (assignedCases ?? []).map((item) => {
                  const proposalId = (item?.proposal_data as any)?.proposal_id as string | undefined;
                  const to = proposalId
                    ? `/proposals/${proposalId}`
                    : `/proposals/new?caseId=${item.id}`;

                  const dbPriority = typeof item?.priority === "string" ? item.priority.toUpperCase() : undefined;
                  const heuristic =
                    item.status === "final_proposal_sent"
                      ? "HIGH"
                      : item.status === "proposal_sent"
                        ? "MEDIUM"
                        : "LOW";
                  const displayPriority = dbPriority || heuristic;

                  return (
                    <Link key={item.id} to={to} className="block">
                      <div className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-muted/50">
                        <div className="space-y-1">
                          <p className="font-medium">{item.case_number}</p>
                          <p className="text-sm text-muted-foreground">{item.client_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={displayPriority === "HIGH" ? "destructive" : "secondary"}>
                            {displayPriority}
                          </Badge>
                          <Badge variant="outline" className="border-sales text-sales">
                            {String(item.status).replace(/_/g, " ")}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </>
            )}
            
            {userDepartment === "operations" && [
              { caseNumber: "CASE-2025-0018", client: "Davis Family", status: "starting_reservations", priority: "high" },
              { caseNumber: "CASE-2025-0016", client: "Brown Group", status: "pre_booking_check", priority: "medium" },
              { caseNumber: "CASE-2025-0014", client: "Miller Travel", status: "client_in_house", priority: "urgent" },
            ].map((item) => (
              <div
                key={item.caseNumber}
                className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-muted/50"
              >
                <div className="space-y-1">
                  <p className="font-medium">{item.caseNumber}</p>
                  <p className="text-sm text-muted-foreground">{item.client}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={item.priority === "urgent" ? "destructive" : item.priority === "high" ? "default" : "secondary"}
                  >
                    {item.priority}
                  </Badge>
                  <Badge variant="outline" className="border-operations text-operations">
                    {item.status.replace(/_/g, " ")}
                  </Badge>
                </div>
              </div>
            ))}
            
            {userDepartment === "finance" && [
              { caseNumber: "CASE-2025-0025", client: "Garcia Family", status: "final_proposal_sent", priority: "high", action: "Invoice Pending" },
              { caseNumber: "CASE-2025-0023", client: "Anderson Group", status: "paid_ready_for_operations", priority: "completed", action: "Payment Confirmed" },
            ].map((item) => (
              <div
                key={item.caseNumber}
                className="flex items-center justify-between rounded-lg border border-border p-4 transition-colors hover:bg-muted/50"
              >
                <div className="space-y-1">
                  <p className="font-medium">{item.caseNumber}</p>
                  <p className="text-sm text-muted-foreground">{item.client}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={item.priority === "completed" ? "default" : "secondary"}>
                    {item.action}
                  </Badge>
                  <Badge variant="outline" className="border-finance text-finance">
                    {item.status.replace(/_/g, " ")}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffDashboard;
